define(['backbone', 'spin', 'errors'], (Backbone, Spinner, Errors) => {

    const Control = Backbone.View.extend({

        adapter: {
            arrayRegex: /(.+)\[(\d+)]$/,

            subscribe (model, attr, callback) {
                const matched = attr.match(this.arrayRegex);
                if (matched) {
                    model.on(`change:${matched[1]}`, callback);
                } else {
                    model.on(`change:${attr}`, callback);
                }
            },

            unsubscribe (model, attr, callback) {
                const matched = attr.match(this.arrayRegex);
                if (matched) {
                    model.off(`change:${matched[1]}`, callback);
                } else {
                    model.off(`change:${attr}`, callback);
                }
            },

            read (model, attr) {
                const matched = attr.match(this.arrayRegex);
                if (matched && model.has(matched[1])) {
                    return model.get(matched[1])[Number(matched[2])];
                }
                return model.get(attr);
            },

            publish (model, attr, value, options) {
                const matched = attr.match(this.arrayRegex);
                if (matched) {
                    const arr = _.clone(model.get(matched[1]));
                    arr[Number(matched[2])] = value;
                    return model.set(matched[1], arr, options);
                }
                return model.set(attr, value, options);
            }
        },

        defaultBinder: {
            bind () {},
            unbind () {},
            routine () {}
        },

        defaultFormatter: {
            read (value) {
                return value;
            },
            publish (value) {
                return value;
            }
        },

        defaults: {
            preload: true
        },

        binders: {
            html (view, value) {
                view.$el.html(value);
            },
            text (view, value) {
                view.$el.html(_.escape(value));
            },
            toggle (view, value) {
                view.$el.toggle(Boolean(value));
            },
            enabled (view, value) {
                view.$el.attr('disabled', !value);
            },
            class (view, value) {
                const enable = value.shift();
                const classes = value.join(' ');

                view.$el.toggleClass(classes, enable);
            },
            switchClass (view, value) {

                /*
                 * switch between two classes separeted by space
                 * e.g. bind-switch-class=".viewModel:toggled | args red green"
                 * toggles red class when viewModel:toggled is true, and green otherwise
                 */
                const enable = value.shift();
                view.$el.toggleClass(value[0], enable);
                view.$el.toggleClass(value[1], !enable);
            },
            attr (view, value) {
                const attr = {};
                attr[value] = value.shift();

                view.$el.attr(attr);
            },
            click: {
                bind (view, publish) {
                    this.callback = function () {
                        publish();
                    };
                    view.$el.on('click', this.callback);
                },
                unbind (view) {
                    view.$el.off('click', this.callback);
                },
                routine () {

                    // Nothing to do here
                }
            },
            lock (view, value) {
                view.toggleLockMask(value);
            }
        },

        initialize () {
            _.defaults(this.options, this.defaults);
            this.isBinded = false;
            this.bindings = [];
        },

        /**
         * Create binding of `model` attribute `attr` to control `binder` with specified `formatters`. Formatters
         * can be one- (declared as function) or two-way (declared as object with `read` and `publish` functions).
         * When reading from model formatters are applied to value from left to right, when publishing to model -
         * from right to left (in this case one-way formatters are ignored).
         *
         * Note: This method only creates binding and adds it to control bindings list. It doesn't actually
         * perform any event subscription. To make bindings active call method `applyBindings`.
         */
        bind (binder, model, attr, formatters) {

            /*
             * With this "hack" for value-binders it is not required to explicitly set opt-error-model
             * and opt-error-path attributes for control node.
             */
            const matched = attr.match(this.adapter.arrayRegex);
            if (binder === 'value') {

                // Increase attr index by 1, because errors, received from backend contains indexes starts with 1
                _.defaults(this.options, {
                    errorModel: model,
                    errorPath: matched ? `${matched[1]}.${Number(matched[2]) + 1}` : attr
                });
            }

            binder = this.binders[binder];

            // Check that formatters are array
            if (!formatters) {
                formatters = [];
            } else if (!_.isArray(formatters)) {
                formatters = [formatters];
            }

            // Cast every formatter to object structure
            formatters = formatters.map(function (formatter) {
                return _.extend({}, this.defaultFormatter, _.isFunction(formatter) ? {read: formatter} : formatter);
            }, this);

            // Cast binder to object structure
            binder = _.extend({}, this.defaultBinder, _.isFunction(binder) ? {routine: binder} : binder);

            // Create binding object
            const self = this;
            const callback = function (_model, _value, options) {
                if (options && options.sender === self.cid) {
                    return;
                }
                const value = formatters.reduce(
                    (memo, formatter) => formatter.read(memo), self.adapter.read(model, attr)
                );
                binder.routine(self, value, model);
            };
            const publish = function (value) {
                value = formatters.reduceRight((memo, formatter) => formatter.publish(memo), value);
                self.adapter.publish(model, attr, value, {sender: self.cid});
            };

            this.bindings.push({binder, model, attr, callback, publish});
            return this;
        },

        /**
         * Performs event subscription for each binding added to control with `bind` method.
         */
        applyBindings () {
            _.each(this.bindings, function (binding) {
                this.adapter.subscribe(binding.model, binding.attr, binding.callback);
                binding.binder.bind(this, binding.publish);
            }, this);

            if (this.options.errorModel && this.options.errorPath) {
                this.listenTo(this.options.errorModel, 'error invalid', this.handleErrors);
            }

            this.isBinded = true;
            return this;
        },

        /**
         * Cancels event subscription for each binding added to control with `bind` method.
         */
        cancelBindings () {
            if (this.isBinded) {
                _.each(this.bindings, function (binding) {
                    binding.binder.unbind(this);
                    this.adapter.unsubscribe(binding.model, binding.attr, binding.callback);
                }, this);

                if (this.options.errorModel && this.options.errorPath) {
                    this.stopListening(this.options.errorModel, 'error invalid', this.handleErrors);
                }

                this.isBinded = false;
            }
            return this;
        },

        /**
         * Performs initial data reading from model attributes to controls via registered bindings.
         */
        preload () {
            _.invoke(this.bindings, 'callback');
            return this;
        },

        render () {
            return this;
        },

        remove () {
            this.removeErrors();
            this.cancelBindings();
            return Backbone.View.prototype.remove.apply(this, arguments);
        },

        renderErrors (messages) {
            this.removeErrors();
            this.errorEls = messages.map((message) => $('<span>').addClass('text-error').
                text(message));
            this.$el.after(this.errorEls);
        },

        removeErrors () {
            if (this.errorEls) {
                _.invoke(this.errorEls, 'remove');
            }
            this.errorEls = null;
        },

        updateAttributes (attributes) {

            // Merge CSS classes
            if (attributes.class) {
                attributes.class = '{0} {1} {2}'.format(
                    this.className || '',
                    this.attributes && this.attributes.class ? this.attributes.class : '',
                    attributes.class
                );
            }
            this.$el.attr(attributes);
            this.attributes = _.extend({}, this.attributes, attributes);
            return this;
        },

        handleErrors (model, response) {
            if (!response.status || response.status < 500) {
                const errors = Errors.getErrors(response); let messages;

                messages = _.chain(
                    this.options.errorPath.split(',')
                ).map(
                    path => path.split('.').reduce(
                        (memo, part) => memo && part in memo ? memo[part] : null, errors
                    )
                ).
                    reduce((memo, msgs) => memo.concat(msgs), []).
                    filter((message) => message && message.length).
                    value();

                if (messages && messages.length) {
                    this.renderErrors(messages);
                }
            }
        },

        toggleLockMask (enable) {
            if (enable && this.lockMask || !enable && !this.lockMask) {
                return;
            }

            if (enable) {
                const spinner = new Spinner();
                this.$el.css('position', 'relative');
                this.lockMask = $('<div>').addClass('drop-mask drop-mask-locking').
                    hide().
                    appendTo(this.$el).
                    fadeIn();
                spinner.spin(this.lockMask.get(0));
            } else {
                this.lockMask.remove();
                this.lockMask = null;
            }
        }
    });

    Control.extend = function (protoProps) {

        _.each(['events', 'defaults', 'binders'], (attr) => {
            protoProps[attr] = _.extend({}, this.prototype[attr], protoProps[attr]);
        });

        return Backbone.View.extend.apply(this, arguments);
    };

    return Control;
});
